import React from 'react';
import { useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import { Avatar, Button, Dialog, DialogActions, DialogContent, DialogTitle, FormControlLabel, Radio, RadioGroup, TextField } from '@material-ui/core';
import { addBook, editBook } from '../redux/actions';
import { Book, BookTemplate } from '../types';


const useStyles = makeStyles(({
  cover: {
    margin: '1rem 0',
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
  },
  image: {
    width: 'auto',
    height: 'auto',
  },
  textfield: {
    width: '80%',
    margin: '1rem 10%',
  },
  radiobuttons: {
    display: 'flex',
    justifyContent: 'center',
  },
}));


function isBook(tbd: Book | BookTemplate): tbd is Book {
    if ((tbd as Book)._links) {
        return true;
    }
    return false;
}


const STATUS_LABELS = ['backlog', 'reading', 'completed'];

type bookFormProps = {
    defaultValues: Book | BookTemplate;
    isOpen: boolean;
    closeForm: () => void;
}


const BookForm = ({ defaultValues, isOpen, closeForm }: bookFormProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();
  const coverImage = isBook(defaultValues) && defaultValues.coverImage;

  const [formTitle, setFormTitle] = React.useState<string>(defaultValues.title);
  const [formAuthors, setFormAuthors] = React.useState<string>(defaultValues.authors.toString());
  const [formStatus, setFormStatus] = React.useState<string>(defaultValues.status);
  const [error, setError] = React.useState<string>('');

  React.useEffect(() => {
      setFormTitle(defaultValues.title);
      setFormAuthors(defaultValues.authors.toString());
      setFormStatus(defaultValues.status);
  }, [defaultValues, setFormTitle, setFormAuthors, setFormStatus]);

  const handleChangeTitle = (title: string) => {
    if (!title) {
        setError('Required field');
    }
    setFormTitle(title);
  }

  const handleChangeAuthors = (authors: string) => {
    if (!authors) {
        setError('Required field');
    }
    setFormAuthors(authors);
  }

  const handleChangeStatus = (event: React.ChangeEvent<HTMLInputElement>) => {
    setFormStatus((event.target as HTMLInputElement).value);
  }

  const handleClose = () => {
    setFormTitle(defaultValues.title);
    setFormAuthors(defaultValues.authors.toString());
    closeForm();
  }

  const handleSave = () => {
      if (!error) {
        const newValues = { 
            ...defaultValues, 
            title: formTitle, 
            authors: formAuthors,
            status: formStatus,
        };
        if (isBook(newValues)) {
            dispatch(editBook(newValues));
        } else {
            dispatch(addBook(newValues));
        }
        handleClose();
      }
  }
  
  return (
      <Dialog open={isOpen} onClose={closeForm}>
          {
              !isBook(defaultValues) &&
              <DialogTitle>Add new book</DialogTitle>
          }
          {
            isBook(defaultValues) && coverImage &&
            <DialogContent className={classes.cover}>
                <Avatar className={classes.image} alt={ defaultValues.title } src={ coverImage } variant='rounded' />
            </DialogContent>
          }
          <DialogContent>
            <TextField 
                className={classes.textfield}
                id="create-new-task"
                error={formTitle.trim().length === 0}
                helperText={formTitle.trim().length === 0 && error}
                onBlur={(event) => handleChangeTitle(event.target.value)}
                placeholder="Title"
                defaultValue={defaultValues.title}
                variant="outlined" 
            />
            <TextField 
                className={classes.textfield}
                id="create-new-task"
                error={formAuthors.trim().length === 0}
                helperText={formAuthors.trim().length === 0 && error}
                onBlur={(event) => handleChangeAuthors(event.target.value)}
                placeholder="Author (use comma to separate multiple authors)"
                defaultValue={defaultValues.authors.toString()} 
            />
          </DialogContent>
          <DialogContent>
            <RadioGroup 
                className={classes.radiobuttons}
                row 
                aria-label="status" 
                name="status" 
                defaultValue={defaultValues.status}
                onChange={handleChangeStatus}
            >
              {
                STATUS_LABELS.map((label, index) => (
                    <FormControlLabel
                        key={index}
                        control={<Radio color="primary" />}
                        label={label}
                        labelPlacement="top"
                        value={label}
                    />
                ))
              }
            </RadioGroup>
          </DialogContent>
          <DialogActions>
            <Button onClick={handleClose} color="primary">
                Cancel
            </Button>
            <Button onClick={handleSave} color="primary">
                Save
            </Button>
          </DialogActions>
      </Dialog>
  );
}

export default BookForm;